import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import OccupationModal from './OccupationModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Occupation {
  occupation_id: number;
  occupation_name: string;
  occupation_status: number;
}

const Occupations: React.FC = () => {
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Occupation | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const cancel: PopconfirmProps['onCancel'] = () => showError('Action cancelled');

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchOccupations = async () => {
    try {
      const data = await apiRequest<Occupation[]>({
        method: 'get',
        url: 'occupation',
      });
      setOccupations(data);
    } catch (error) {
      showError('Failed to fetch occupations');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchOccupations();
  }, []);

  const handleEditClick = (record: Occupation) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchOccupations();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (occupation_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-occupation/${occupation_id}`,
      });
      showSuccess('Occupation deleted');
      fetchOccupations();
    } catch {
      showError('Failed to delete occupation');
    }
  };

  const handleStatusToggle = async (occupation_id: number) => {
    setOccupations((prev) =>
      prev.map((item) =>
        item.occupation_id === occupation_id
          ? { ...item, occupation_status: item.occupation_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-occupation/${occupation_id}`,
      });
      showSuccess('Occupation status updated');
    } catch (error) {
      showError('Failed to update status');
      setOccupations((prev) =>
        prev.map((item) =>
          item.occupation_id === occupation_id
            ? { ...item, occupation_status: item.occupation_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Occupation>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<Occupation> = [
    {
      title: 'Occupation Name',
      dataIndex: 'occupation_name',
      key: 'occupation_name',
      filters: Array.from(new Set(occupations.map((s) => s.occupation_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.occupation_name || null,
      onFilter: (value, record) => record.occupation_name.includes(value as string),
      sorter: (a, b) => a.occupation_name.localeCompare(b.occupation_name),
      sortOrder: sortedInfo.columnKey === 'occupation_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.occupation_name}</div>,
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Occupation) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.occupation_status}
                onChange={() => handleStatusToggle(record.occupation_id)}
              />
              <Button onClick={() => handleEditClick(record)}>
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this occupation?"
              onConfirm={() => handleDelete(record.occupation_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        
        <Breadcrumb
            style={{ margin: 0 }}
            items={[
              {
                href: "/",
                title: <HomeOutlined />,
              },
              {
                title: "View Occupations",
              },
            ]}
          />

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Occupation
            </Button>
          </Flex>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Occupation>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="occupation_id"
          dataSource={occupations.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No occupations found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, occupations.length)} of{' '}
                {occupations.length}
              </span>
              <Pagination
                {...pagination}
                total={occupations.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <OccupationModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Occupations;
